import { t as uiText } from '../../i18n';
import { db } from '../../lib/firebase';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import * as Y from 'yjs';

/**
 * 本文が空のMemo_数字パターンのページを削除する関数
 * editDataを参照して正確な削除判定を行う
 */
export const deleteNoContentMemos = async (workspaceId: string): Promise<{ success: boolean; deletedCount: number; error?: string }> => {
  try {
    // ワークスペース内のボードコレクションを参照
    const boardsRef = collection(db, 'workspaces', workspaceId, 'boards');

    // Memo_パターンにマッチするボードを取得
    const memoQuery = query(
      boardsRef,
      where('editData.title', '>=', 'Memo_'),
      where('editData.title', '<=', 'Memo_\uf8ff')
    );

    const memoSnapshot = await getDocs(memoQuery);

    const docsToDelete: Array<{ id: string; data: any }> = [];
    let memoPatternCount = 0;
    let skippedCount = 0;

    // 削除対象をフィルタリング（Memo_数字パターンで本文が空のもののみ）
    memoSnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      
      // editDataからタイトルを取得
      const title = data.editData?.title || '';
      
      // タイトルがMemo_数字パターンかチェック
      const isMemo = /^Memo_\d+$/.test(title);
      
      if (isMemo) {
        memoPatternCount++;

        // 段階的な空判定を実行
        const isEmpty = checkIfMemoIsEmpty(data, docSnapshot.id);

        // ハッシュタグや内部リンクがないかチェック
        const hasHashtags = data.searchData?.hashtags && Array.isArray(data.searchData.hashtags) && data.searchData.hashtags.length > 0;
        const hasInternalLinks = data.searchData?.internalLinks && Array.isArray(data.searchData.internalLinks) && data.searchData.internalLinks.length > 0;
        const hasExternalLinks = data.searchData?.externalLinks && Array.isArray(data.searchData.externalLinks) && data.searchData.externalLinks.length > 0;

        // 添付ファイルや画像がないかチェック
        const hasAttachments = data.previewData?.previewMeta?.hasAttachments === true && 
                              data.previewData?.previewMeta?.attachmentCount > 0;
        const hasImages = data.previewData?.previewMeta?.hasImages === true && 
                         data.previewData?.previewMeta?.imageCount > 0;

        // アーカイブされていないかチェック
        const isArchived = data.archived === true;

        // ピンされていないかチェック
        const isPinned = data.pinned === true;

        // 削除条件: 本文もハッシュタグもリンクも添付ファイルも画像もなく、アーカイブもピンもされていない
        if (isEmpty && !hasHashtags && !hasInternalLinks && !hasExternalLinks && !hasAttachments && !hasImages && !isArchived && !isPinned) {
          docsToDelete.push({
            id: docSnapshot.id,
            data: data
          });
        } else {
          skippedCount++;
          const skipReason = getSkipReason(isEmpty, hasHashtags, hasInternalLinks, hasExternalLinks, hasAttachments, hasImages, isArchived, isPinned);
        }
      }
    });

    // 削除実行
    if (docsToDelete.length > 0) {
      const deletePromises = docsToDelete.map(async ({ id }) => {
        // workspaces/{workspaceId}/boards/{id} を削除
        const boardDocRef = doc(db, 'workspaces', workspaceId, 'boards', id);
        await deleteDoc(boardDocRef);

        // publicPages/{workspaceId}/pages/{id} も削除（存在する場合）
        try {
          const publicPageDocRef = doc(db, 'publicPages', workspaceId, 'pages', id);
          await deleteDoc(publicPageDocRef);
          console.log(`✅ deleteNoContentMemos: 公開ページも削除しました (${id})`);
        } catch (publicPageError: any) {
          // エラーの詳細をログ出力
          console.error(`❌ deleteNoContentMemos: 公開ページ削除エラー (${id})`, {
            error: publicPageError,
            code: publicPageError?.code,
            message: publicPageError?.message,
            path: `publicPages/${workspaceId}/pages/${id}`
          });
        }
      });

      await Promise.all(deletePromises);
    } else {}

    return { success: true, deletedCount: docsToDelete.length };
  } catch (error) {
    console.error('❌ deleteNoContentMemos: エラーが発生しました:', error);
    return { 
      success: false, 
      deletedCount: 0, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * 段階的な空判定を実行する関数
 * 1. searchData.normalizedContent を最優先でチェック
 * 2. previewData.content を2番目にチェック
 * 3. Y.Doc復元・textContent抽出を最後にチェック
 */
const checkIfMemoIsEmpty = (data: any, boardId: string): boolean => {
  // 1. searchData.normalizedContent を最優先でチェック
  const searchContent = data.searchData?.normalizedContent || '';

  if (searchContent.trim().length > 0) {
    return false; // 削除対象外
  }

  // 2. previewData.content をチェック
  const previewContent = data.previewData?.content || [];

  if (Array.isArray(previewContent) && previewContent.length > 0) {
    // プレビューコンテンツに実際のテキストがあるかチェック
    const hasPreviewText = previewContent.some(item => {
      if (typeof item === 'string' && item.trim().length > 0) return true;
      if (typeof item === 'object' && item !== null) {
        // オブジェクト形式のコンテンツの場合、実際のテキストを抽出
        if (item.type === 'paragraph') {
          // contentプロパティが存在しない場合は空と判定
          if (!item.content || !Array.isArray(item.content) || item.content.length === 0) {
            return false; // 空の段落
          }
          const paragraphText = extractTextFromParagraph(item);
          return paragraphText.trim().length > 0;
        }
        // その他のオブジェクトは空でないと判定
        const itemStr = JSON.stringify(item);
        return itemStr.length > 20;
      }
      return false;
    });

    if (hasPreviewText) {
      return false; // 削除対象外
    }
  }

  // 3. Y.Doc復元・textContent抽出を最後にチェック
  if (data.editData?.ydocBytes) {
    try {
      const ydocBytes = data.editData.ydocBytes;
      if (ydocBytes && ydocBytes._byteString) {
        const ydoc = new Y.Doc();
        Y.applyUpdate(ydoc, ydocBytes._byteString);

        // textContentを抽出
        const textContent = ydoc.getText('default').toString();
        const isEmpty = textContent.trim().length === 0;

        return isEmpty;
      } else {
        return true;
      }
    } catch (error) {
      console.warn(`⚠️ checkIfMemoIsEmpty: Y.Doc復元エラー`, { boardId, error });
      return true; // 削除対象
    }
  }

  return true; // 削除対象
};

/**
 * 段落ノードからテキストを抽出する関数
 */
const extractTextFromParagraph = (paragraphNode: any): string => {
  // contentプロパティが存在しない場合は空文字列を返す
  if (!paragraphNode.content || !Array.isArray(paragraphNode.content) || paragraphNode.content.length === 0) {
    return '';
  }
  
  return paragraphNode.content
    .map((item: any) => {
      if (item && typeof item === 'object') {
        if (item.type === 'text' && item.text) {
          return item.text;
        }
        if (item.type === 'autoLink' && item.attrs?.linkText) {
          return item.attrs.linkText;
        }
        if (item.type === 'hardBreak') {
          return '\n';
        }
        // ネストしたコンテンツを再帰的に処理
        if (item.content) {
          return extractTextFromParagraph(item);
        }
      }
      return '';
    })
    .join('');
};

/**
 * スキップ理由を取得する関数（デバッグ用）
 */
const getSkipReason = (
  isEmpty: boolean, 
  hasHashtags: boolean, 
  hasInternalLinks: boolean, 
  hasExternalLinks: boolean, 
  hasAttachments: boolean, 
  hasImages: boolean, 
  isArchived: boolean, 
  isPinned: boolean
): string => {
  const reasons = [];
  
  if (!isEmpty) reasons.push(uiText("Has content"));
  if (hasHashtags) reasons.push(uiText("Has hashtag"));
  if (hasInternalLinks) reasons.push(uiText("Has internal links"));
  if (hasExternalLinks) reasons.push(uiText("Has external link"));
  if (hasAttachments) reasons.push(uiText("Has attachments"));
  if (hasImages) reasons.push(uiText("Has image"));
  if (isArchived) reasons.push(uiText("Archived"));
  if (isPinned) reasons.push(uiText("Pinned"));
  
  return reasons.length > 0 ? reasons.join(', ') : uiText("Unknown");
};
